// Excel на справката „Прехвърляния": редовете такива, каквито са на екрана —
// филтрираните, намерените и подредените (`shown`), с колоните на широкия
// изглед (посоката разделена на От склад / Към склад), и последен ред
// „Общо за периода · без анулирани" със сумите от реда „Общо" на таблицата.
// Анулираните остават, със статус и причината за анулиране.
import type { TFunction } from "i18next";
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import type { ExcelColumn } from "../../../../utils/excelExport";
import { fmtDateTime } from "../../components/dateTime";
import { excelMoney } from "../../components/list/excelCells";
import {
  fromWarehouseOf,
  isTransferAnnulled,
  toWarehouseOf,
  transferUserOf,
} from "../../transfer/components/transferMeta";
import { transferTotals } from "./transfersReportModel";

export type TransferExcelRow = {
  // № на документа; в реда „Общо" — етикетът му
  number: number | string;
  date: string;
  from: string;
  to: string;
  user: string;
  note: string;
  products: number | null;
  net: number;
  gross: number;
  status: string;
  annulReason: string;
};

export type TransferExcelLabels = { annulled: string; total: string };

export const transferExcelRows = (transfers: TransferDocument[], labels: TransferExcelLabels): TransferExcelRow[] => {
  const rows = transfers.map((transfer): TransferExcelRow => {
    const annulled = isTransferAnnulled(transfer);
    return {
      number: transfer.id,
      date: fmtDateTime(transfer.date),
      from: fromWarehouseOf(transfer),
      to: toWarehouseOf(transfer),
      user: transferUserOf(transfer),
      note: transfer.note ?? "",
      products: transfer.productsCount ?? 0,
      net: excelMoney(transfer.totalValueNoVat),
      gross: excelMoney(transfer.totalValueWithVat),
      status: annulled ? labels.annulled : "",
      annulReason: annulled ? (transfer.deleteReason ?? "") : "",
    };
  });
  if (!rows.length) return rows;
  const totals = transferTotals(transfers);
  return [
    ...rows,
    {
      number: labels.total,
      date: "",
      from: "",
      to: "",
      user: "",
      note: "",
      products: null,
      net: excelMoney(totals.net),
      gross: excelMoney(totals.gross),
      status: "",
      annulReason: "",
    },
  ];
};

export const transferExcelColumns = (t: TFunction): ExcelColumn<TransferExcelRow>[] => [
  { header: "№", value: (row) => row.number },
  { header: t("inventoryPage.colDateTime"), value: (row) => row.date, width: 17 },
  { header: t("inventoryPage.colFromWarehouse"), value: (row) => row.from },
  { header: t("inventoryPage.colToWarehouse"), value: (row) => row.to },
  { header: t("inventoryPage.colUser"), value: (row) => row.user },
  { header: t("inventoryPage.colComment"), value: (row) => row.note, width: 28 },
  { header: t("inventoryPage.colProducts"), value: (row) => row.products },
  { header: t("inventoryPage.colNet"), value: (row) => row.net },
  { header: t("inventoryPage.colGross"), value: (row) => row.gross },
  { header: t("inventoryPage.colStatus"), value: (row) => row.status },
  { header: t("inventoryPage.tbColAnnulReason"), value: (row) => row.annulReason, width: 28 },
];
